import { BackButton } from "@/components/BackButton";
import { SoundToggle } from "@/components/SoundToggle";
import { playResult, playFanfare } from "@/lib/feedbackSounds";
import React, { useMemo, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, CheckCircle2, XCircle, Lock, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { getCourse, getInstrument } from "@/data/curriculum";
import { buildPaper } from "@/lib/theory";
import { api } from "@/lib/api";

const Question = ({ q, n, pick, onPick, marked }) => (
  <li className="rounded-2xl border border-border bg-card p-5 print:break-inside-avoid print:border-black" data-testid={`paper-q-${n}`}>
    <p className="font-medium text-foreground"><span className="mr-2 font-mono text-primary">{n}.</span>{q.prompt}</p>
    {q.seq && <p className="mt-2 font-mono text-sm text-muted-foreground">{q.seq.join("  ")}</p>}
    <div className="mt-3 flex flex-wrap gap-2">
      {q.options.map((o, k) => {
        const chosen = pick === o;
        const tone = marked ? (o === q.answer ? "border-emerald-500 bg-emerald-500/15" : chosen ? "border-red-500 bg-red-500/10" : "border-border") : chosen ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary";
        return (
          <button key={o} disabled={marked} onClick={() => onPick(o)} data-testid={`paper-q-${n}-opt-${k}`} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${tone}`}>
            <span className="mr-1.5 font-mono text-xs opacity-70">{"abc"[k]})</span>{o}
          </button>
        );
      })}
    </div>
    {marked && (pick === q.answer
      ? <p className="mt-2 flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 size={13} /> Correct</p>
      : <p className="mt-2 flex items-center gap-1 text-xs text-red-600"><XCircle size={13} /> Answer: {q.answer}</p>)}
  </li>
);

export default function PracticePaper() {
  const { instrumentId, courseId } = useParams();
  const { user, purchases } = useAuth();
  const course = getCourse(instrumentId, courseId);
  const instrument = getInstrument(instrumentId);
  const qs = useMemo(() => (course ? buildPaper(course) : []), [course]);
  const [picks, setPicks] = useState({});
  const [marked, setMarked] = useState(false);
  const [best, setBest] = useState(null);
  const key = `${instrumentId}:${courseId}`;
  const owned = user && (user.role === "admin" || (purchases || []).includes(key));
  useEffect(() => { if (owned) api.get("/theory/status").then(({ data }) => setBest(data.papers[key] || null)).catch(() => {}); }, [owned, key]);
  if (!course) return <div className="p-10 text-center">Course not found.</div>;
  const score = qs.filter((q) => picks[q.id] === q.answer).length;
  const submit = async () => {
    setMarked(true);
    if (score === qs.length) playFanfare(0.1, "Theory practice paper"); else playResult(score >= qs.length / 2);
    try { const { data } = await api.post("/theory/paper", { course_key: key, score, total: qs.length }); setBest(data.papers[key]); toast.success(`You scored ${score}/${qs.length}`); }
    catch (e) { toast.error("Score could not be saved"); }
  };
  if (!owned) return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="paper-locked">
      <BackButton testId="paper-locked-back-button" fallback={`/courses/${instrumentId}/${courseId}`} />
      <Lock className="mx-auto mt-6 text-primary" /><h1 className="mt-4 font-serif text-3xl">Theory practice paper</h1>
      <p className="mt-2 text-muted-foreground">Available to students on this course.</p>
      <Link to={`/courses/${instrumentId}/${courseId}`}><Button className="mt-6 rounded-full">View the course</Button></Link>
    </div>
  );
  return (
    <div className="mx-auto max-w-3xl px-6 pb-24 pt-10 print:p-0" data-testid="practice-paper">
      <div className="mb-5 print:hidden"><BackButton testId="paper-back-button" fallback={`/courses/${instrumentId}/${courseId}`} /></div>
      <Logo height={56} />
      <h1 className="mt-6 font-serif text-4xl tracking-tight sm:text-5xl">Theory practice paper</h1>
      <p className="mt-2 text-muted-foreground">{instrument.label} · {course.title} — {qs.length} questions</p>
      <div className="mt-5 flex flex-wrap items-center gap-3 print:hidden">
        <SoundToggle testId="paper-sound-toggle" />
        <Button variant="outline" onClick={() => window.print()} className="rounded-full" data-testid="paper-print"><Printer size={15} className="mr-1.5" /> Print paper</Button>
        {best && <span className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-sm text-primary" data-testid="paper-best"><Trophy size={14} /> Best: {best.best}/{best.total}</span>}
      </div>
      <ol className="mt-8 space-y-4">
        {qs.map((q, i) => <Question key={q.id} q={q} n={i + 1} pick={picks[q.id]} marked={marked} onPick={(o) => setPicks((p) => ({ ...p, [q.id]: o }))} />)}
      </ol>
      <div className="mt-8 flex flex-wrap items-center gap-3 print:hidden">
        {!marked ? (
          <Button onClick={submit} disabled={Object.keys(picks).length < qs.length} className="rounded-full" data-testid="paper-submit">Mark my paper ({Object.keys(picks).length}/{qs.length} answered)</Button>
        ) : (
          <>
            <p className="font-serif text-2xl" data-testid="paper-score">You scored {score}/{qs.length}</p>
            <Button variant="outline" className="rounded-full" onClick={() => { setPicks({}); setMarked(false); window.scrollTo(0, 0); }} data-testid="paper-retry">Try again</Button>
          </>
        )}
      </div>
    </div>
  );
}
